import { useClientAuth } from "@crm/access/auth/context";

/**
 * useAuth Hook - 获取认证状态
 *
 * 混合策略：
 * - 服务器端：从 Router Context 读取 beforeLoad 填充的认证状态
 * - 客户端：使用 Better Auth 的 useSession hook，避免重复查询数据库
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isAuthenticated } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <div>请先登录</div>;
 *   }
 *
 *   return <div>你好，{user.name}</div>;
 * }
 * ```
 */
export function useAuth() {
  const auth = useClientAuth();
  return auth;
}

/**
 * useIsAuthenticated Hook - 只获取登录状态
 *
 * @example
 * ```tsx
 * function NavBar() {
 *   const isAuthenticated = useIsAuthenticated();
 *
 *   return (
 *     <nav>
 *       {isAuthenticated ? <UserMenu /> : <LoginButton />}
 *     </nav>
 *   );
 * }
 * ```
 */
export function useIsAuthenticated() {
  const auth = useClientAuth();
  return auth.isAuthenticated;
}

/**
 * useUserRole Hook - 只获取用户角色
 *
 * @example
 * ```tsx
 * function AdminPanel() {
 *   const userRole = useUserRole();
 *
 *   if (userRole !== "admin") {
 *     return <div>权限不足</div>;
 *   }
 *
 *   return <AdminDashboard />;
 * }
 * ```
 */
export function useUserRole() {
  const auth = useClientAuth();
  return auth.userRole;
}

/**
 * useCurrentUser Hook - 只获取当前用户信息
 *
 * @example
 * ```tsx
 * function UserProfile() {
 *   const user = useCurrentUser();
 *
 *   if (!user) {
 *     return <div>未登录</div>;
 *   }
 *
 *   return (
 *     <div>
 *       <h1>{user.name}</h1>
 *       <p>{user.email}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useCurrentUser() {
  const auth = useClientAuth();
  return auth.user;
}

/**
 * useUserId Hook - 只获取用户 ID
 *
 * @example
 * ```tsx
 * function MyStudents() {
 *   const userId = useUserId();
 *
 *   const { data } = useQuery({
 *     queryKey: studentKeys.detail(userId),
 *     queryFn: () => fetchMyStudents(userId),
 *     enabled: !!userId,
 *   });
 *
 *   return <StudentList students={data} />;
 * }
 * ```
 */
export function useUserId() {
  const auth = useClientAuth();
  return auth.userId;
}
